'use client';
import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Share2, MessageCircle, Facebook, Twitter, Download, Check } from 'lucide-react';

interface Props {
  url: string;
  username: string;
  memberName: string;
  accent: string;
}

export default function QRCodeSection({ url, username, memberName, accent }: Props) {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'qr' | 'share'>('qr');

  const qrUrl = url ? `${url}?qr=1` : '';
  const shareText = `Join ${memberName} on RaketNow — the complete digital business ecosystem! 🚀`;

  const handleCopy = () => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('member-qr-code');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.onload = () => {
      ctx?.drawImage(img, 0, 0);
      const a = document.createElement('a');
      a.download = `raketnow-${username}-qr.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  };

  const shareLinks = [
    {
      label: 'WhatsApp',
      icon: MessageCircle,
      color: '#25D366',
      href: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + url)}`,
    },
    {
      label: 'Facebook',
      icon: Facebook,
      color: '#1877F2',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`,
    },
    {
      label: 'Twitter/X',
      icon: Twitter,
      color: '#000',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
    },
  ];

  return (
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: '#E2E8F0' }}>
        {(['qr', 'share'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="flex-1 py-3 text-sm font-semibold transition-all"
            style={{
              color: activeTab === tab ? accent : '#94A3B8',
              borderBottom: activeTab === tab ? `2px solid ${accent}` : '2px solid transparent',
              background: 'transparent',
            }}
          >
            {tab === 'qr' ? '📱 QR Code' : '🔗 Share Page'}
          </button>
        ))}
      </div>

      <div className="p-6">
        {activeTab === 'qr' ? (
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* QR Code */}
            <div className="flex-shrink-0 p-4 rounded-2xl shadow-inner" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
              {qrUrl ? (
                <QRCodeSVG
                  id="member-qr-code"
                  value={qrUrl}
                  size={160}
                  level="H"
                  fgColor="#0F172A"
                  bgColor="#F8FAFC"
                  imageSettings={{
                    src: '/assets/images/raketnow-1781807404875.png',
                    x: undefined,
                    y: undefined,
                    height: 32,
                    width: 32,
                    excavate: true,
                  }}
                />
              ) : (
                <div className="w-40 h-40 flex items-center justify-center" style={{ color: '#94A3B8' }}>
                  <span className="text-sm">Loading QR...</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-bold text-base mb-1" style={{ color: '#0F172A' }}>Your Referral QR Code</h3>
              <p className="text-sm mb-4" style={{ color: '#64748B' }}>
                Share this QR code to let people scan and visit your personal RaketNow page. Every scan is tracked automatically.
              </p>
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                <button
                  onClick={handleDownloadQR}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: accent }}
                >
                  <Download size={13} /> Download QR
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                  style={{ background: '#F1F5F9', color: '#475569' }}
                >
                  {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
              <div className="mt-3 px-3 py-2 rounded-lg text-xs font-mono truncate" style={{ background: '#F8FAFC', color: '#64748B', border: '1px solid #E2E8F0' }}>
                {url || `raketnow.org/r/${username}`}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="font-bold text-base mb-1" style={{ color: '#0F172A' }}>Share Your Page</h3>
            <p className="text-sm mb-5" style={{ color: '#64748B' }}>Share your personal RaketNow page and grow your network.</p>

            {/* URL copy */}
            <div className="flex gap-2 mb-5">
              <div className="flex-1 px-3 py-2.5 rounded-xl text-sm font-mono truncate" style={{ background: '#F8FAFC', color: '#64748B', border: '1px solid #E2E8F0' }}>
                {url || `raketnow.org/r/${username}`}
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white flex-shrink-0"
                style={{ background: accent }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {/* Social share buttons */}
            <div className="flex flex-wrap gap-3">
              {shareLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 hover:scale-105"
                  style={{ background: s.color }}
                >
                  <s.icon size={15} />
                  {s.label}
                </a>
              ))}
              <button
                onClick={async () => {
                  if (navigator.share) {
                    try {
                      await navigator.share({ title: `Join ${memberName} on RaketNow`, url });
                    } catch {
                      handleCopy();
                    }
                  } else {
                    handleCopy();
                  }
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: '#F1F5F9', color: '#475569' }}
              >
                <Share2 size={15} /> More
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
